import React, { useState, useContext, useEffect } from "react";
import {
  Dialog, DialogActions, Button, TextField, Box, Typography,
  IconButton, CircularProgress, InputAdornment,
} from "@material-ui/core";
import { Close, OpenInNew, Visibility, VisibilityOff, CheckCircle } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(() => ({
  dialogPaper: { borderRadius: 16, maxWidth: 560, width: "100%" },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "18px 22px 12px", borderBottom: "1px solid #f0f0f0",
  },
  titleText: { fontWeight: 700, fontSize: 18 },
  subtitle: { fontSize: 12, color: "#6b7280", marginTop: 2 },
  content: { padding: "18px 22px 8px", backgroundColor: "#fafafa", maxHeight: "65vh", overflowY: "auto" },
  section: {
    backgroundColor: "#fff", borderRadius: 10, padding: 16,
    marginBottom: 14, border: "1px solid #e5e7eb",
  },
  sectionTitle: {
    fontWeight: 600, fontSize: 13, color: "#111827",
    display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
  },
  badge: {
    width: 20, height: 20, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0,
  },
  caption: { fontSize: 11, color: "#6b7280", lineHeight: 1.6 },
  actions: { padding: "12px 22px", backgroundColor: "#fafafa", borderTop: "1px solid #f0f0f0" },
  connectBtn: { fontWeight: 600, textTransform: "none", borderRadius: 8 },
  configuredBox: {
    backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0",
    borderRadius: 10, padding: "12px 16px", marginBottom: 14,
    display: "flex", alignItems: "center", gap: 8,
  },
  field: { marginBottom: 10 },
}));

const COLOR = "#EA4335";

const GoogleAdsModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [configured, setConfigured] = useState(null);
  const [configuredCustomerId, setConfiguredCustomerId] = useState("");
  const [form, setForm] = useState({ developerToken: "", customerId: "", clientId: "", clientSecret: "", refreshToken: "" });
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ developerToken: "", customerId: "", clientId: "", clientSecret: "", refreshToken: "" });
    api.get("/ads/google/config").then(({ data }) => {
      setConfigured(data.configured);
      if (data.configured) setConfiguredCustomerId(data.customerId || "");
    }).catch(() => setConfigured(false));
  }, [open]);

  const setField = (key) => (e) => { const v = e.target.value; setForm(f => ({ ...f, [key]: v })); };

  const handleSave = async () => {
    const missing = Object.entries(form).find(([, v]) => !v.trim());
    if (missing) { toast.error("Preencha todos os campos"); return; }
    setSaving(true);
    try {
      await api.post("/ads/google/config", {
        developerToken: form.developerToken.trim(),
        customerId: form.customerId.trim(),
        clientId: form.clientId.trim(),
        clientSecret: form.clientSecret.trim(),
        refreshToken: form.refreshToken.trim(),
      });
      toast.success("Google Ads conectado com sucesso!");
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    try {
      await api.delete("/ads/google/config");
      setConfigured(false);
      setConfiguredCustomerId("");
      toast.success("Integração removida");
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.dialogPaper }}>
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.titleText}>
            <span style={{ marginRight: 8 }}>🟡</span>Configurar Google Ads
          </Typography>
          <Typography className={classes.subtitle}>
            Métricas de campanhas da conta Google Ads
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><Close /></IconButton>
      </Box>

      <Box className={classes.content}>
        {configured && (
          <Box className={classes.configuredBox}>
            <CheckCircle style={{ color: "#16a34a", fontSize: 20 }} />
            <Box>
              <Typography style={{ fontWeight: 600, fontSize: 13, color: "#15803d" }}>
                Já configurado — Conta: {configuredCustomerId}
              </Typography>
              <Typography style={{ fontSize: 11, color: "#166534" }}>
                Preencha os campos abaixo para atualizar as credenciais
              </Typography>
            </Box>
          </Box>
        )}

        {/* Step 1 */}
        <Box className={classes.section}>
          <Box className={classes.sectionTitle}>
            <Box className={classes.badge} style={{ backgroundColor: COLOR }}>1</Box>
            Obter Developer Token
          </Box>
          <Typography className={classes.caption}>
            1. Acesse sua conta <strong>Google Ads</strong> (conta gerenciadora MCC)<br />
            2. Vá em <strong>Ferramentas → Centro de API</strong><br />
            3. Copie o <strong>Developer Token</strong> (pode estar pendente de aprovação para acesso básico)
          </Typography>
          <Button
            size="small" variant="outlined" startIcon={<OpenInNew style={{ fontSize: 14 }} />}
            style={{ marginTop: 8, textTransform: "none", fontSize: 12 }}
            onClick={() => window.open("https://ads.google.com/aw/apicenter", "_blank")}
          >
            Abrir Centro de API
          </Button>
        </Box>

        {/* Step 2 */}
        <Box className={classes.section}>
          <Box className={classes.sectionTitle}>
            <Box className={classes.badge} style={{ backgroundColor: COLOR }}>2</Box>
            Configurar OAuth 2.0 no Google Cloud
          </Box>
          <Typography className={classes.caption}>
            1. Acesse o <strong>Google Cloud Console</strong> e crie (ou selecione) um projeto<br />
            2. Ative a <strong>Google Ads API</strong><br />
            3. Em <strong>APIs e Serviços → Credenciais</strong>, crie um ID de cliente OAuth 2.0 (tipo: Aplicativo da Web)<br />
            4. Gere o <strong>Refresh Token</strong> usando o OAuth Playground com o escopo:<br />
            &nbsp;&nbsp;<code style={{ fontSize: 10, color: "#374151" }}>https://www.googleapis.com/auth/adwords</code>
          </Typography>
          <Button
            size="small" variant="outlined" startIcon={<OpenInNew style={{ fontSize: 14 }} />}
            style={{ marginTop: 8, textTransform: "none", fontSize: 12 }}
            onClick={() => window.open("https://console.cloud.google.com/apis/credentials", "_blank")}
          >
            Abrir Cloud Console
          </Button>
        </Box>

        {/* Step 3 */}
        <Box className={classes.section}>
          <Box className={classes.sectionTitle}>
            <Box className={classes.badge} style={{ backgroundColor: COLOR }}>3</Box>
            Inserir credenciais
          </Box>

          <Box className={classes.field}>
            <TextField label="Developer Token" variant="outlined" fullWidth size="small"
              type={showToken ? "text" : "password"}
              value={form.developerToken} onChange={setField("developerToken")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowToken(v => !v)}>
                      {showToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box className={classes.field}>
            <TextField label="Customer ID (ex: 123-456-7890)" variant="outlined" fullWidth size="small"
              value={form.customerId} onChange={setField("customerId")} />
          </Box>
          <Box className={classes.field}>
            <TextField label="Client ID" variant="outlined" fullWidth size="small"
              value={form.clientId} onChange={setField("clientId")} />
          </Box>
          <Box className={classes.field}>
            <TextField label="Client Secret" variant="outlined" fullWidth size="small"
              type="password" value={form.clientSecret} onChange={setField("clientSecret")} />
          </Box>
          <TextField label="Refresh Token" variant="outlined" fullWidth size="small"
            type="password" value={form.refreshToken} onChange={setField("refreshToken")} />
        </Box>
      </Box>

      <DialogActions className={classes.actions}>
        {configured && (
          <Button onClick={handleRemove} style={{ color: "#ef4444", textTransform: "none" }}>
            Remover integração
          </Button>
        )}
        <Box flex={1} />
        <Button onClick={onClose} style={{ textTransform: "none" }}>Fechar</Button>
        <Button
          variant="contained"
          className={classes.connectBtn}
          style={{ backgroundColor: COLOR, color: "#fff" }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Salvar configuração"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoogleAdsModal;
